import React, { useMemo, useState } from 'react';
import { Button, Form, Input, Tooltip } from 'antd';
import { EnvironmentOutlined, ExportOutlined, GlobalOutlined } from '@ant-design/icons';

import { ITicketLocation } from 'models/SupportTicket';
import {
  buildMapsEmbedUrl,
  buildMapsUrl,
  formatLatLng,
  hasTicketLocation,
  isShortMapsLink,
  parseLatLng
} from '../utilTicket';

// Giá trị form của ô vị trí: coordText là link Google Maps hoặc "lat, lng" người dùng dán vào,
// tọa độ thật được parse ra lúc submit (toTicketLocation).
export interface ILocationFormValue {
  address: string;
  coordText: string;
}

export const EMPTY_LOCATION: ILocationFormValue = { address: '', coordText: '' };

export const locationFromTicket = (loc?: ITicketLocation | null): ILocationFormValue => ({
  address: loc?.address || '',
  coordText: hasTicketLocation(loc) ? formatLatLng(loc) : ''
});

/** Chuyển giá trị form -> payload API. Không có gì thì trả undefined để không gửi field. */
export const toTicketLocation = (value?: ILocationFormValue | null): ITicketLocation | undefined => {
  const address = value?.address?.trim() || '';
  const coords = parseLatLng(value?.coordText);
  if (!address && !coords) return undefined;
  return { address, lat: coords?.lat ?? null, lng: coords?.lng ?? null };
};

/** Lỗi khi người dùng có nhập ô tọa độ nhưng không đọc được. */
export const validateLocationCoord = (value?: ILocationFormValue | null): string | undefined => {
  const text = value?.coordText?.trim();
  if (!text || parseLatLng(text)) return undefined;
  return isShortMapsLink(text)
    ? 'Link rút gọn không có tọa độ, hãy mở link rồi copy link đầy đủ hoặc dán "lat, lng"'
    : 'Không đọc được tọa độ, dán link Google Maps đầy đủ hoặc "10.762622, 106.660172"';
};

interface ILocationFieldProps {
  value: ILocationFormValue;
  onChange: (value: ILocationFormValue) => void;
  onBlur?: () => void;
  /** form: label kiểu Form.Item (form tạo phiếu); compact: label nhỏ như panel chi tiết phiếu */
  variant?: 'form' | 'compact';
}

export default function LocationField({ value, onChange, onBlur, variant = 'form' }: ILocationFieldProps) {
  const [showMap, setShowMap] = useState(false);
  const loc = value || EMPTY_LOCATION;
  const coords = useMemo(() => parseLatLng(loc.coordText), [loc.coordText]);
  const coordError = validateLocationCoord(loc);

  const set = (patch: Partial<ILocationFormValue>) => onChange({ ...loc, ...patch });

  const coordHelp = coordError || (coords ? `Tọa độ: ${formatLatLng(coords)}` : undefined);

  const addressInput = (
    <Input
      value={loc.address}
      onChange={e => set({ address: e.target.value })}
      onBlur={onBlur}
      placeholder="VD: Bãi xe số 4, Cảng Cát Lái, TP. Thủ Đức"
      allowClear
    />
  );

  const coordInput = (
    <div className="flex gap-1">
      <Input
        value={loc.coordText}
        status={coordError ? 'error' : undefined}
        onChange={e => set({ coordText: e.target.value })}
        onBlur={onBlur}
        placeholder="Link Google Maps hoặc 10.762622, 106.660172"
        allowClear
      />
      <Tooltip title="Mở Google Maps">
        <Button
          icon={<ExportOutlined />}
          disabled={!coords}
          href={coords ? buildMapsUrl(coords) : undefined}
          target="_blank"
        />
      </Tooltip>
      <Tooltip title={showMap ? 'Ẩn bản đồ' : 'Xem bản đồ vệ tinh'}>
        <Button
          icon={<GlobalOutlined />}
          type={showMap && coords ? 'primary' : 'default'}
          disabled={!coords}
          onClick={() => setShowMap(s => !s)}
        />
      </Tooltip>
    </div>
  );

  const map = showMap && coords && (
    <iframe
      title="Bản đồ vệ tinh vị trí xe"
      src={buildMapsEmbedUrl(coords)}
      className="w-full h-48 border border-gray-200 rounded block"
      loading="lazy"
      referrerPolicy="no-referrer-when-downgrade"
    />
  );

  if (variant === 'compact') {
    return (
      <div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <div className="text-[11px] text-gray-500 mb-0.5">
              <EnvironmentOutlined className="text-red-500 mr-1" />
              Địa chỉ xe / nơi hỗ trợ
            </div>
            {addressInput}
          </div>
          <div>
            <div className="text-[11px] text-gray-500 mb-0.5">Tọa độ (link Google Maps hoặc lat, lng)</div>
            {coordInput}
            {coordHelp && (
              <div className={`text-[11px] mt-0.5 ${coordError ? 'text-red-500' : 'text-green-700'}`}>{coordHelp}</div>
            )}
          </div>
        </div>
        {map && <div className="mt-2">{map}</div>}
      </div>
    );
  }

  return (
    <>
      <div className="grid grid-cols-2 gap-2">
        <Form.Item label="Địa chỉ xe / nơi hỗ trợ">{addressInput}</Form.Item>
        <Form.Item
          label="Tọa độ (link Google Maps hoặc lat, lng)"
          validateStatus={coordError ? 'error' : coords ? 'success' : ''}
          help={coordHelp}
        >
          {coordInput}
        </Form.Item>
      </div>
      {map && <div className="mb-4 -mt-2">{map}</div>}
    </>
  );
}
